import { registrationIdIsStateWasteCode, registrationStateOmitsFormAndDot } from "./constants";

/**
 * The dimensions the state-registration matcher scores a candidate on. Shared
 * so the scorer and the dialog's scoring-details table can never disagree about
 * how much each dimension is worth.
 */
export interface RegistrationMatchWeights {
  /** Federal waste code alignment — the primary signal. */
  code: number;
  /** DOT/UN number, a secondary identity anchor. */
  dot: number;
  /** Waste-description (name) similarity. */
  description: number;
  /** Form code — lowest weight, and tolerated when absent. */
  form: number;
  /**
   * State waste code. Active only for states where the registration's own id is
   * a state waste code; inactive (0) everywhere else.
   */
  stateCode: number;
}

/** The four base dimensions every state scores on. They sum to 1. */
const BASE_WEIGHTS: RegistrationMatchWeights = {
  code: 0.6,
  dot: 0.2,
  description: 0.15,
  form: 0.05,
  stateCode: 0,
};

/**
 * The weight the state waste code takes in states where it is the
 * registration's own id — the single strongest identity signal available, so it
 * takes half the total.
 */
const STATE_CODE_WEIGHT = 0.5;

/**
 * The dimension weights to use for a state. Every returned set sums to 1, so a
 * score is directly comparable across states.
 */
export function registrationMatchWeights(
  state: string | null | undefined
): RegistrationMatchWeights {
  if (registrationIdIsStateWasteCode(state)) {
    // The state waste code IS the registration's id, so it carries half the
    // weight. The base four keep their relative proportions within the
    // remaining half: they sum to 1, so scaling them all by 0.5 preserves their
    // ratios (code .30 / dot .10 / description .075 / form .025).
    const scale = 1 - STATE_CODE_WEIGHT;
    return {
      stateCode: STATE_CODE_WEIGHT,
      code: BASE_WEIGHTS.code * scale,
      dot: BASE_WEIGHTS.dot * scale,
      description: BASE_WEIGHTS.description * scale,
      form: BASE_WEIGHTS.form * scale,
    };
  }
  if (registrationStateOmitsFormAndDot(state)) {
    // The registry publishes no form code and no DOT info, so those two
    // dimensions can never carry a signal here. Drop them and redistribute
    // their weight across the two that remain, rather than capping every
    // candidate with neutral scores on dimensions the state doesn't provide.
    const kept = BASE_WEIGHTS.code + BASE_WEIGHTS.description;
    return {
      code: BASE_WEIGHTS.code / kept,
      description: BASE_WEIGHTS.description / kept,
      dot: 0,
      form: 0,
      stateCode: 0,
    };
  }
  // No state is both state-code-keyed and form/DOT-less, so the two branches
  // above never interact.
  return BASE_WEIGHTS;
}

/**
 * The same weights expressed as percentages, for display alongside a candidate's
 * per-dimension subscores. Derived from `registrationMatchWeights` rather than
 * restated, so the numbers a user sees are the ones the scorer actually used.
 * Not rounded — a state can carry fractional percentages (TX: 7.5 / 2.5) and
 * rounding here would stop the column summing to 100.
 */
export function registrationMatchWeightPercentages(
  state: string | null | undefined
): RegistrationMatchWeights {
  const weights = registrationMatchWeights(state);
  return {
    code: weights.code * 100,
    dot: weights.dot * 100,
    description: weights.description * 100,
    form: weights.form * 100,
    stateCode: weights.stateCode * 100,
  };
}
